import { supabase } from "@/integrations/supabase/client";
import { generateTwilioSid } from "./realisticDataGenerators";

export interface RealDataContext {
  user: {
    id: string;
    email: string;
    role: string;
  };
  tenant: {
    tenant_id: string;
    name: string;
    twilio_sid?: string;
    region: string;
  };
  campaigns: Array<{
    campaign_id: string;
    name: string;
    status: string;
    utm_source?: string;
    utm_medium?: string;
  }>;
  ads: Array<{
    id: string;
    name: string;
    campaign_id: string;
    status: string;
  }>;
  numbers: Array<{
    number_id: string;
    e164: string;
    area_code?: string;
    status: string;
  }>;
  onboarding: {
    tenant_id: string;
    current_step: string;
    forwarding_number?: string;
    recording_enabled?: boolean;
    retention_days?: number;
  } | null;
}

/**
 * Loads real user account data for testing purposes
 * Returns actual tenant, campaign, ad, and number data from the authenticated user's account
 */
export async function loadRealUserData(): Promise<RealDataContext | null> {
  try {
    // Get current user
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError || !user) {
      console.warn('[RealDataLoader] No authenticated user found');
      return null;
    }

    // Get current tenant ID
    const { data: tenantId, error: tenantError } = await supabase.rpc('calie_get_current_tenant_id');
    if (tenantError || !tenantId) {
      console.warn('[RealDataLoader] No active tenant found');
      return null;
    }

    // Get user profile data
    const { data: userProfile, error: userProfileError } = await supabase
      .from('calie_users')
      .select('*')
      .eq('user_id', user.id)
      .maybeSingle();

    if (userProfileError) {
      console.warn('[RealDataLoader] Failed to load user profile:', userProfileError);
    }

    // Get tenant data
    const { data: tenant, error: tenantDataError } = await supabase
      .from('calie_tenants')
      .select('*')
      .eq('tenant_id', tenantId)
      .maybeSingle();

    if (tenantDataError || !tenant) {
      console.warn('[RealDataLoader] Failed to load tenant data:', tenantDataError);
      return null;
    }

    // Get campaigns
    const { data: campaigns, error: campaignsError } = await supabase
      .from('calie_campaigns')
      .select('*')
      .eq('tenant_id', tenantId)
      .order('created_at', { ascending: false });

    if (campaignsError) {
      console.warn('[RealDataLoader] Failed to load campaigns:', campaignsError);
    }

    // Get ads
    const { data: ads, error: adsError } = await supabase
      .from('calie_ads')
      .select('*')
      .eq('tenant_id', tenantId)
      .order('created_at', { ascending: false });

    if (adsError) {
      console.warn('[RealDataLoader] Failed to load ads:', adsError);
    }

    // Get numbers
    const { data: numbers, error: numbersError } = await supabase
      .from('calie_numbers')
      .select('*')
      .eq('tenant_id', tenantId)
      .order('created_at', { ascending: false });

    if (numbersError) {
      console.warn('[RealDataLoader] Failed to load numbers:', numbersError);
    }

    // Get onboarding state
    const { data: onboarding, error: onboardingError } = await supabase
      .from('calie_onboarding_state')
      .select('*')
      .eq('tenant_id', tenantId)
      .maybeSingle();

    if (onboardingError) {
      console.warn('[RealDataLoader] Failed to load onboarding state:', onboardingError);
    }

    const realData: RealDataContext = {
      user: {
        id: user.id,
        email: user.email || '',
        role: userProfile?.role || 'business_owner',
      },
      tenant,
      campaigns: campaigns || [],
      ads: ads || [],
      numbers: numbers || [],
      onboarding,
    };

    console.log('[RealDataLoader] Successfully loaded real user data:', {
      userEmail: realData.user.email,
      tenantName: realData.tenant.name,
      campaignCount: realData.campaigns.length,
      adCount: realData.ads.length,
      numberCount: realData.numbers.length,
    });

    return realData;
  } catch (error) {
    console.error('[RealDataLoader] Failed to load real user data:', error);
    return null;
  }
}

/**
 * Generate realistic test payloads using real user account data
 */
export function generateRealTestPayloads(realData: RealDataContext) {
  const firstCampaign = realData.campaigns[0];
  const firstAd = realData.ads[0];
  const firstNumber = realData.numbers[0];

  return {
    // Twilio credential validation using real tenant with realistic SID format
    twilioValidation: {
      tenantId: realData.tenant.tenant_id,
      // Use real SID if available, otherwise generate realistic format
      accountSid: realData.tenant.twilio_sid || generateTwilioSid('AC'),
      authToken: 'test_auth_token_32_characters_long'
    },

    // Auto subaccount creation using real tenant name
    autoCreateSubaccount: {
      tenantName: realData.tenant.name,
      region: realData.tenant.region
    },

    // Number purchase using real tenant and area codes from existing numbers
    buyNumber: {
      areaCode: firstNumber?.area_code || '415',
      tenantId: realData.tenant.tenant_id
    },

    // Buy number for specific ad (using real ad ID)
    buyNumberForAd: {
      areaCode: firstNumber?.area_code || '415',
      adId: firstAd?.id || 'no-ads-available',
      campaignId: firstCampaign?.campaign_id || 'no-campaigns-available'
    },

    // Configure voice webhook for real tenant
    configureVoiceWebhook: {
      tenantId: realData.tenant.tenant_id
    },

    // Sync numbers for real tenant
    syncNumbers: {
      tenantId: realData.tenant.tenant_id
    },

    // Reset onboarding for real tenant
    resetOnboarding: {
      tenantId: realData.tenant.tenant_id
    },

    // Fix credentials for real tenant
    fixCredentials: {
      tenantId: realData.tenant.tenant_id,
      twilioSid: realData.tenant.twilio_sid || generateTwilioSid('AC'),
      twilioAuthToken: 'test_replacement_auth_token_32_chars'
    },

    // DNI script using real campaign data
    dniScript: {
      tenantId: realData.tenant.tenant_id,
      campaignId: firstCampaign?.campaign_id,
      utmSource: firstCampaign?.utm_source || 'test',
      utmMedium: firstCampaign?.utm_medium || 'cpc'
    }
  };
}

/**
 * Get realistic phone number from user's existing numbers or generate appropriate test number
 * Enhanced with comprehensive geographic coverage and realistic formatting
 */
export function getRealPhoneNumber(realData: RealDataContext): string {
  if (realData.numbers.length > 0) {
    return realData.numbers[0].e164;
  }
  
  // Enhanced area code coverage with major metropolitan areas
  const areaCodes = {
    'us-east-1': ['212', '646', '718', '917', '347', '929'], // New York Metro
    'us-west-1': ['415', '510', '650', '408', '669', '628'], // San Francisco Bay Area
    'us-west-2': ['206', '425', '253', '564', '360'], // Seattle/Washington
    'us-central-1': ['312', '773', '630', '847', '224', '708'], // Chicago Metro
    'us-southeast': ['305', '786', '954', '561', '754', '239'], // Florida
    'us-southwest': ['214', '469', '972', '945', '713', '281'], // Texas Metro
    'us-northeast': ['617', '857', '781', '339', '508', '774'], // Boston Metro
    'us-mountain': ['303', '720', '970', '719'], // Colorado/Mountain Time
    'us-pacific': ['213', '323', '310', '424', '661', '818'], // Los Angeles Metro
  };
  
  const regionCodes = areaCodes[realData.tenant.region as keyof typeof areaCodes] || 
                     areaCodes['us-west-1']; // Default to San Francisco area codes
  const areaCode = regionCodes[Math.floor(Math.random() * regionCodes.length)];
  
  // Generate realistic exchange and subscriber numbers
  const exchange = Math.floor(Math.random() * 743) + 200; // Valid exchange codes (200-942)
  const subscriber = Math.floor(Math.random() * 9000) + 1000; // 4-digit subscriber (1000-9999)
  
  return `+1${areaCode}${exchange}${subscriber}`;
}

/**
 * Get real campaign data for testing or return default test campaign
 */
export function getRealCampaignData(realData: RealDataContext) {
  if (realData.campaigns.length > 0) {
    return realData.campaigns[0];
  }
  
  return {
    campaign_id: 'test-campaign-id',
    name: 'Test Campaign',
    status: 'active',
    utm_source: 'test',
    utm_medium: 'cpc'
  };
}

/**
 * Get real ad data for testing or return default test ad
 */
export function getRealAdData(realData: RealDataContext) {
  if (realData.ads.length > 0) {
    return realData.ads[0];
  }
  
  return {
    id: 'test-ad-id',
    name: 'Test Ad',
    campaign_id: getRealCampaignData(realData).campaign_id,
    status: 'active'
  };
}